import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector/LanguageSelector";
import Spinner from "../../../components/Spinner/Spinner";
import {
  acceptInvitation,
  getInvitation,
} from "../../../services/invitationService";
import TokenService from "../../../services/token.service";
import { UserContext } from "../../../utils/contexts/UserContext";
import useAuth from "../../../utils/hooks/useAuth";
import { formatApiError } from "../../../utils/helpers/apiErrors";
import telosWordmark from "../../../assets/telos-wordmark.svg";

// Inline SVGs (project convention is no lucide-react).
const svgProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  viewBox: "0 0 24 24",
  "aria-hidden": "true",
};
const LockIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const EyeIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);
const AlertIcon = ({ className }) => (
  <svg className={className} {...svgProps}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const CARD =
  "bg-white rounded-2xl shadow-xl shadow-slate-200/60 p-8 border border-slate-100";
const FIELD =
  "w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#00adff] focus:ring-2 focus:ring-[#00adff]/25 outline-none transition disabled:opacity-60";
const LABEL =
  "block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2";

const MIN_PASSWORD_LENGTH = 8;

export default function Invite() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setUserContext } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  // `expired` covers every reason the link does not work. The API answers
  // unknown / expired / already-used / superseded identically on purpose, so
  // the page must not invent a more specific explanation than it was given.
  const [expired, setExpired] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: { password: "", confirm: "" } });

  useEffect(() => {
    if (user?.uuid) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getInvitation({ token, signal: controller.signal })
      .then((data) => {
        setInvitation(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        // Only a 404 means the link itself is dead. A 429 — the per-IP budget,
        // which a whole school shares while everyone sets passwords at once —
        // or a 5xx is temporary, and calling it "expired" sends people to ask
        // for a replacement link that fails exactly the same way.
        if (err?.response?.status === 404) setExpired(true);
        else setUnavailable(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [token]);

  const onSubmit = useCallback(
    async (data) => {
      setSubmitting(true);
      setServerError(null);
      try {
        const tokens = await acceptInvitation({
          token,
          body: { password: data.password },
        });
        TokenService.updateLocalAccessToken(tokens.access);
        TokenService.updateLocalRefreshToken(tokens.refresh);
        await setUserContext(tokens.access);
        navigate("/", { replace: true });
      } catch (err) {
        // A 404 here means the link was spent or expired between loading the
        // page and submitting it — show the dead-link state rather than a
        // validation error against a form that can no longer succeed.
        if (err?.response?.status === 404) {
          setExpired(true);
          return;
        }
        setServerError(formatApiError(err, t("auth.invite.genericError")));
      } finally {
        setSubmitting(false);
      }
    },
    [token, setUserContext, navigate, t],
  );

  const shell = (children) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50 to-blue-100 p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-3">
          <LanguageSelector />
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className={`${CARD} flex justify-center`}>
        <Spinner label={t("auth.invite.checking")} />
      </div>,
    );
  }

  if (unavailable) {
    return shell(
      <div className={`${CARD} text-center space-y-4`}>
        <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
          <AlertIcon className="w-6 h-6 text-slate-500" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">
            {t("auth.invite.unavailableTitle")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {t("auth.invite.unavailableBody")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full py-2.5 rounded-xl bg-[#0f2e54] text-white text-sm font-semibold shadow-lg shadow-[#0f2e54]/30 hover:bg-[#0c2543] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition"
        >
          {t("auth.invite.retry")}
        </button>
      </div>,
    );
  }

  if (expired) {
    return shell(
      <div className={`${CARD} text-center space-y-4`}>
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
          <AlertIcon className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">
            {t("auth.invite.expiredTitle")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {t("auth.invite.expiredBody")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="w-full py-2.5 rounded-xl bg-[#0f2e54] text-white text-sm font-semibold shadow-lg shadow-[#0f2e54]/30 hover:bg-[#0c2543] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition"
        >
          {t("auth.invite.backToSignIn")}
        </button>
      </div>,
    );
  }

  const greetingName = [invitation?.first_name, invitation?.last_name]
    .filter(Boolean)
    .join(" ");

  return shell(
    <form onSubmit={handleSubmit(onSubmit)} className={`${CARD} space-y-5`}>
      <div className="text-center pb-2">
        <img src={telosWordmark} alt="Telos" className="h-10 w-auto mx-auto" />
        <h1 className="mt-4 text-lg font-semibold text-slate-900">
          {greetingName
            ? t("auth.invite.greeting", { name: greetingName })
            : t("auth.invite.greetingFallback")}
        </h1>
        <p className="mt-1.5 text-sm text-slate-500">
          {invitation?.organization
            ? t("auth.invite.subtitleWithOrg", {
                organization: invitation.organization,
              })
            : t("auth.invite.subtitle")}
        </p>
      </div>

      {/* Shown, not editable: the username is assigned, and a new user who
          cannot see it has no way to sign in again later. */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {t("auth.username")}
        </p>
        <p className="text-sm text-slate-800 font-medium">
          {invitation?.username}
        </p>
      </div>

      <div>
        <label htmlFor="password" className={LABEL}>
          {t("auth.invite.newPassword")}
        </label>
        <div className="relative">
          <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            disabled={submitting}
            {...register("password", {
              required: t("auth.passwordRequired"),
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: t("auth.invite.tooShort", {
                  count: MIN_PASSWORD_LENGTH,
                }),
              },
            })}
            className={FIELD}
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
            aria-label={t("auth.invite.togglePassword")}
          >
            {showPassword ? (
              <EyeOffIcon className="w-4 h-4" />
            ) : (
              <EyeIcon className="w-4 h-4" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="mt-1.5 text-xs text-rose-600">
            {errors.password.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="confirm" className={LABEL}>
          {t("auth.invite.confirmPassword")}
        </label>
        <div className="relative">
          <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            id="confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            disabled={submitting}
            {...register("confirm", {
              required: t("auth.invite.confirmRequired"),
              validate: (value) =>
                value === watch("password") || t("auth.invite.mismatch"),
            })}
            className={FIELD}
            placeholder="••••••••"
          />
        </div>
        {errors.confirm && (
          <p className="mt-1.5 text-xs text-rose-600">
            {errors.confirm.message}
          </p>
        )}
      </div>

      {serverError && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-2.5 rounded-xl bg-[#0f2e54] text-white text-sm font-semibold shadow-lg shadow-[#0f2e54]/30 hover:bg-[#0c2543] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00adff]/50 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? t("auth.invite.saving") : t("auth.invite.submit")}
      </button>
    </form>,
  );
}
